import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, Plug, Lock, Users, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const { api } = useAuth();
  const [settings, setSettings] = useState({ email: "", password: "", qris_string: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [newUserUsername, setNewUserUsername] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    Promise.all([
      api("get", "/admin/settings"),
      api("get", "/admin/users"),
    ])
      .then(([settingsRes, usersRes]) => {
        setSettings(settingsRes.data);
        setUsers(usersRes.data.users || []);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
        setUsersLoading(false);
      });
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api("put", "/admin/settings", {
        email: settings.email,
        password: settings.password,
        qris_string: settings.qris_string,
      });
      toast.success("Settings berhasil disimpan!");
    } catch {
      toast.error("Gagal menyimpan settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api("post", "/admin/settings/test-connection");
      res.data.success ? toast.success(res.data.message) : toast.error(res.data.message);
    } catch {
      toast.error("Gagal test koneksi");
    } finally {
      setTesting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return;
    setChangingPw(true);
    try {
      await api("post", "/admin/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password berhasil diubah!");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah password");
    } finally {
      setChangingPw(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserUsername.trim() || !newUserPassword) return;
    setCreatingUser(true);
    try {
      const res = await api("post", "/admin/users", {
        username: newUserUsername.trim(),
        password: newUserPassword,
        role: "admin",
      });
      setUsers([res.data, ...users]);
      setNewUserUsername("");
      setNewUserPassword("");
      toast.success("User berhasil dibuat!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat user");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Hapus user ${username}?`)) return;
    try {
      await api("delete", `/admin/users/${username}`);
      setUsers(users.filter((u) => u.username !== username));
      toast.success("User berhasil dihapus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus user");
    }
  };

  if (loading) return <div className="text-muted-foreground text-sm">Memuat settings...</div>;

  return (
    <div data-testid="settings-page" className="space-y-6 sm:space-y-8">
      <div className="rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Settings</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">Konfigurasi GoBiz dan admin</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Atur kredensial, QRIS statis, dan password admin dari satu halaman.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-3xl border border-border/70 bg-card/90 shadow-sm">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <Plug size={16} />
            <h3 className="font-heading text-lg font-bold">GoBiz Credentials</h3>
          </div>
          <div className="space-y-4 p-5">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Email GoBiz</label>
              <input
                data-testid="settings-email"
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                placeholder="email@merchant.com"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Password GoBiz</label>
              <input
                data-testid="settings-password"
                type="password"
                value={settings.password}
                onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                placeholder="Password akun GoBiz"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">QRIS Static String</label>
              <textarea
                data-testid="settings-qris"
                value={settings.qris_string}
                onChange={(e) => setSettings({ ...settings, qris_string: e.target.value })}
                className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20 font-mono"
                rows={5}
                placeholder="00020101021126610014COM.GO-JEK.WWW..."
              />
              <p className="mt-1 text-xs text-muted-foreground">String QRIS statis dari akun GoPay Merchant kamu</p>
            </div>
            <div className="flex flex-col gap-3 pt-2 sm:flex-row">
              <button
                data-testid="save-settings-button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                <Save size={14} /> {saving ? "Menyimpan..." : "Simpan"}
              </button>
              <button
                data-testid="test-connection-button"
                onClick={handleTest}
                disabled={testing}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm font-semibold transition hover:bg-secondary disabled:opacity-50"
              >
                <Plug size={14} /> {testing ? "Testing..." : "Test Koneksi"}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-border/70 bg-card/90 shadow-sm xl:col-span-2">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <Users size={16} />
            <h3 className="font-heading text-lg font-bold">User Login</h3>
          </div>
          <div className="p-5 space-y-5">
            <form onSubmit={handleCreateUser} className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Username Baru</label>
                <input
                  type="text"
                  value={newUserUsername}
                  onChange={(e) => setNewUserUsername(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                  placeholder="misal: ops1"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                  placeholder="password user"
                />
              </div>
              <button
                type="submit"
                disabled={creatingUser}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                <Plus size={14} /> {creatingUser ? "Membuat..." : "Buat User"}
              </button>
            </form>

            <div className="space-y-3">
              {usersLoading ? (
                <p className="text-sm text-muted-foreground">Memuat user...</p>
              ) : users.length === 0 ? (
                <p className="text-sm text-muted-foreground">Belum ada user.</p>
              ) : (
                users.map((user) => (
                  <div key={user.username} className="flex flex-col gap-3 rounded-2xl border border-border bg-background/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold">{user.username}</p>
                      <p className="text-xs text-muted-foreground">Role: {user.role || "admin"} {user.active === false ? "· nonaktif" : ""}</p>
                    </div>
                    {user.username !== "admin" && (
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(user.username)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-background/80 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={14} /> Hapus
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-border/70 bg-card/90 shadow-sm">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <Lock size={16} />
            <h3 className="font-heading text-lg font-bold">Ubah Password Admin</h3>
          </div>
          <div className="p-5">
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Password Lama</label>
                <input
                  data-testid="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Password Baru</label>
                <input
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                />
              </div>
              <button
                data-testid="change-password-button"
                type="submit"
                disabled={changingPw}
                className="inline-flex items-center justify-center rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                {changingPw ? "Mengubah..." : "Ubah Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
